/**
 * Phone number utility functions for normalizing and formatting phone numbers
 * across the application.
 */

/**
 * Clean phone number by removing all non-digit characters.
 * @param {string} phone - The raw phone number input
 * @returns {string} - Digits only (e.g., "(555) 123-4567" → "5551234567")
 */
export function cleanPhoneNumber(phone) {
    if (!phone) return '';
    return phone.replace(/\D/g, '');
}

/**
 * Normalize phone number to E.164 format required by order services.
 * Handles various input formats and returns +1234567890 format.
 * 
 * @param {string} rawPhone - The raw phone number input (can be in various formats)
 * @returns {string} - Normalized phone number in E.164 format (e.g., "+15551234567")
 * @throws {Error} - If phone number has missing digits or invalid format
 */
export function normalizePhoneForOrder(rawPhone) {
    if (!rawPhone) {
        throw new Error('Phone number is required');
    }
    
    const cleaned = cleanPhoneNumber(rawPhone);
    
    // Validate minimum digit count - must have at least 10 digits
    if (cleaned.length < 10) {
        throw new Error(`Phone number is missing digits. Please correct the phone number. Found ${cleaned.length} digits, need at least 10.`);
    }
    
    // Handle 10-digit numbers (US/Canada) - add +1 prefix
    if (cleaned.length === 10) {
        return '+1' + cleaned;
    }
    
    // Handle 11-digit numbers starting with 1 - add + prefix
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
        return '+' + cleaned;
    }
    
    // Handle numbers already starting with + (E.164 format)
    if (rawPhone.startsWith('+')) {
        if (cleaned.length >= 10 && cleaned.length <= 14) {
            return '+' + cleaned;
        }
        throw new Error(`Phone number has invalid length. Found ${cleaned.length} digits, need 10-14 digits.`);
    }
    
    // If we have more than 11 digits or invalid format, throw error
    if (cleaned.length > 11) {
        throw new Error(`Phone number has too many digits. Found ${cleaned.length} digits, need 10-11 digits.`);
    }
    
    // If we can't normalize (e.g., 11 digits not starting with 1), throw error
    throw new Error(`Invalid phone number format. Please correct the phone number. Found ${cleaned.length} digits.`);
}

/**
 * Validate phone number format for UI feedback.
 * Returns validation status and error message without throwing errors.
 * 
 * @param {string} rawPhone - The raw phone number input
 * @param {boolean} required - Whether the field is required (default: true)
 * @returns {{isValid: boolean, errorMessage: string}} - Validation result
 */
export function validatePhoneNumber(rawPhone, required = true) {
    // Check if empty
    if (!rawPhone || rawPhone.trim() === '') {
        if (required) {
            return {
                isValid: false,
                errorMessage: 'Phone number is required'
            };
        }
        return { isValid: true, errorMessage: '' };
    }
    
    const cleaned = cleanPhoneNumber(rawPhone);
    
    // Handle numbers starting with + (E.164 format, including +1 from InputMask)
    if (rawPhone.startsWith('+')) {
        // For +1 format (US/Canada), we need exactly 11 digits total (1 + 10)
        if (cleaned.startsWith('1')) {
            if (cleaned.length === 11) {
                return { isValid: true, errorMessage: '' };
            }
            if (cleaned.length < 11) {
                // Calculate how many digits are missing after the country code
                const digitsAfterCountryCode = cleaned.length - 1;
                const missingDigits = 10 - digitsAfterCountryCode;
                return {
                    isValid: false,
                    errorMessage: `Phone number is missing ${missingDigits} digit${missingDigits !== 1 ? 's' : ''}. Need 10 digits after area code.`
                };
            }
            return {
                isValid: false,
                errorMessage: `Phone number has too many digits. Found ${cleaned.length} digits, need exactly 11 (including country code).`
            };
        }
        // For other international numbers with +, allow 10-14 digits
        if (cleaned.length >= 10 && cleaned.length <= 14) {
            return { isValid: true, errorMessage: '' };
        }
        return {
            isValid: false,
            errorMessage: `Phone number has invalid length. Found ${cleaned.length} digits, need 10-14 digits.`
        };
    }
    
    // Handle numbers without + prefix
    // Validate minimum digit count - must have at least 10 digits
    if (cleaned.length < 10) {
        return {
            isValid: false,
            errorMessage: `Phone number is missing digits. Found ${cleaned.length} digits, need at least 10.`
        };
    }
    
    // Validate 10-digit numbers (US/Canada) - will get +1 added during normalization
    if (cleaned.length === 10) {
        return { isValid: true, errorMessage: '' };
    }
    
    // Validate 11-digit numbers starting with 1
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
        return { isValid: true, errorMessage: '' };
    }
    
    // If we have more than 11 digits, it's invalid
    if (cleaned.length > 11) {
        return {
            isValid: false,
            errorMessage: `Phone number has too many digits. Found ${cleaned.length} digits, need 10-11 digits.`
        };
    }
    
    // If we can't validate (e.g., 11 digits not starting with 1), it's invalid
    return {
        isValid: false,
        errorMessage: `Invalid phone number format. Found ${cleaned.length} digits.`
    };
}

/**
 * Format phone number for Twilio SMS (E.164 format).
 * This is a wrapper around normalizePhoneForOrder for backward compatibility.
 * 
 * @param {string} rawPhone - The raw phone number input
 * @returns {string} - Formatted phone number (e.g., "+15551234567")
 * @throws {Error} - If phone number is invalid
 */
export function formatPhoneForSms(rawPhone) {
    const normalized = normalizePhoneForOrder(rawPhone);

    // Validate the normalized result matches E.164 format
    if (!normalized.match(/^\+\d{10,14}$/)) {
        throw new Error('Invalid phone number format');
    }

    return normalized;
}

/**
 * Format phone number for display in UI.
 * Converts E.164 or raw phone numbers to (XXX) XXX-XXXX format.
 *
 * @param {string} phone - The phone number (can be E.164, raw digits, or formatted)
 * @returns {string} - Formatted phone number (e.g., "(555) 123-4567")
 */
export function formatPhoneForDisplay(phone) {
    if (!phone) return '';

    const cleaned = cleanPhoneNumber(phone);

    // Handle 11-digit US numbers (with country code)
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
        const areaCode = cleaned.slice(1, 4);
        const prefix = cleaned.slice(4, 7);
        const line = cleaned.slice(7, 11);
        return `(${areaCode}) ${prefix}-${line}`;
    }

    // Handle 10-digit US numbers
    if (cleaned.length === 10) {
        const areaCode = cleaned.slice(0, 3);
        const prefix = cleaned.slice(3, 6);
        const line = cleaned.slice(6, 10);
        return `(${areaCode}) ${prefix}-${line}`;
    }

    // For other formats, return as-is
    return phone;
}
